import API from "@/lib/axios";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { io, type Socket } from "socket.io-client";
import { AxiosError } from "axios";

type SocketFactory = typeof io;

// Optional WebSocket support - gracefully handle if socket.io-client not installed
const socketFactory: SocketFactory | null =
  typeof window === "undefined" ? null : io;

export interface SupportConversation {
  id: number;
  customer_name: string;
  customer_email?: string | null;
  customer_phone?: string | null;
  agent_id?: number | null;
  status: "pending" | "active" | "closed";
  conversation_mode?: "ai" | "live_agent";
  last_message_preview?: string | null;
  last_message_at?: string | null;
  created_at?: string;
}

export interface SupportMessage {
  id: number;
  conversation_id?: number; // Conversation ID for WebSocket messages
  sender_type: "customer" | "agent" | "ai";
  sender_id?: number | null;
  message: string;
  status: string;
  created_at: string;
  read_at?: string | null;
}

// const CONVERSATION_POLL_INTERVAL = 15000; // Unused - WebSocket handles real-time updates
// const MESSAGE_POLL_INTERVAL = 4000; // Unused - WebSocket handles real-time updates
const SOCKET_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:5001";

export const useCustomerSupportChat = () => {
  const { user } = useAuth();
  
  // Get token from localStorage since it's not in AuthContext
  const [token, setToken] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("authToken");
  });
  
  // Update token when user changes (login/logout)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedToken = localStorage.getItem("authToken");
    setToken(storedToken);
  }, [user]);
  
  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  // Removed polling - WebSocket only
  const [searchTerm, setSearchTerm] = useState("");
  
  // WebSocket support - always enabled if socketFactory is available
  const socketRef = useRef<Socket | null>(null);
  const [wsConnected, setWsConnected] = useState(false);

  const fetchConversations = useCallback(async () => {
    setIsLoadingConversations(true);
    setConversationError(null);
    try {
      const res = await API.get("/api/v1/customer-chat/conversations");
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setConversations(list);

      if (!selectedConversationId && list.length) {
        setSelectedConversationId(list[0].id);
      } else if (selectedConversationId && !list.some((conv: SupportConversation) => conv.id === selectedConversationId)) {
        setSelectedConversationId(list.length ? list[0].id : null);
      }
    } catch (error: unknown) {
      console.error("Failed to fetch customer conversations:", error);
      const axiosError = error as AxiosError<{ message?: string }>;
      setConversationError(axiosError.response?.data?.message || "Failed to load conversations");
    } finally {
      setIsLoadingConversations(false);
    }
  }, [selectedConversationId]);

  const fetchMessages = useCallback(async () => {
    if (!selectedConversationId) {
      setMessages([]);
      return;
    }

    setIsLoadingMessages(true);
    setMessagesError(null);

    try {
      const res = await API.get(`/api/v1/customer-chat/conversations/${selectedConversationId}/messages`);
      const data = res.data?.data?.messages ?? [];
      setMessages(Array.isArray(data) ? data : []);
    } catch (error: unknown) {
      console.error("Failed to fetch customer messages:", error);
      const axiosError = error as AxiosError<{ message?: string }>;
      setMessagesError(axiosError.response?.data?.message || "Failed to load messages");
    } finally {
      setIsLoadingMessages(false);
    }
  }, [selectedConversationId]);

  // Initialize WebSocket - always try to connect (WebSocket only, no polling)
  useEffect(() => {
    if (!token || !socketFactory) {
      console.warn("⚠️ WebSocket not initialized: missing token or socketFactory");
      return;
    }

    // Disconnect existing socket if any
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    // Try to create WebSocket connection
    const socket = socketFactory(SOCKET_URL, {
      auth: { token },
      transports: ["websocket"], // WebSocket only - no polling fallback
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity, // Keep trying to reconnect
      reconnectionDelayMax: 5000,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("✅ WebSocket connected for customer support chat");
      setWsConnected(true);
      
      // Join conversation room when connected and conversation is selected
      if (selectedConversationId) {
        socket.emit("join:conversation", { conversationId: selectedConversationId });
        console.log(`📥 Joined conversation room: ${selectedConversationId}`);
      }
    });

    socket.on("disconnect", () => {
      console.log("❌ WebSocket disconnected");
      setWsConnected(false);
      // Try to reconnect - don't fall back to polling
      setTimeout(() => {
        if (socketRef.current && !socketRef.current.connected) {
          console.log("🔄 Attempting to reconnect WebSocket...");
          socketRef.current.connect();
        }
      }, 2000);
    });

    socket.on("connect_error", (error) => {
      console.error("⚠️ WebSocket connection error:", error);
      setWsConnected(false);
      // Retry connection after delay
      setTimeout(() => {
        if (socketRef.current && !socketRef.current.connected) {
          console.log("🔄 Retrying WebSocket connection...");
          socketRef.current.connect();
        }
      }, 3000);
    });

    // Listen for ALL messages (customer, agent, AI) - broadcast to all agents
    socket.on("customer:message:agent", (messageData: SupportMessage) => {
      console.log("📨 Message received via WebSocket (customer:message:agent):", messageData);
      
      // Add message if it's for the selected conversation OR if no conversation is selected (for queue updates)
      if (!selectedConversationId || messageData.conversation_id === selectedConversationId) {
        // Only add to messages list if conversation is selected
        if (selectedConversationId && messageData.conversation_id === selectedConversationId) {
          setMessages((prev) => {
            // Avoid duplicates - check by ID
            if (prev.some((m) => m.id === messageData.id)) {
              console.log("⚠️ Duplicate message ignored:", messageData.id);
              return prev;
            }
            // Add new message to existing list (append, don't replace)
            console.log("✅ Adding new message to list:", messageData.id, messageData.sender_type);
            return [...prev, messageData];
          });
        }
      }
      
      // Always refresh conversations to update last message preview
      fetchConversations();
    });

    // Listen for messages in conversation room (agent messages, AI responses, etc.)
    socket.on("customer:message:received", (data: { conversationId?: number; message?: SupportMessage }) => {
      console.log("📨 Message received in conversation room:", data);
      
      if (data.conversationId === selectedConversationId || data.message) {
        if (data.message) {
          setMessages((prev) => {
            // Avoid duplicates - check by ID
            if (prev.some((m) => m.id === data.message!.id)) {
              console.log("⚠️ Duplicate message ignored:", data.message!.id);
              return prev;
            }
            // Add new message to existing list (append, don't replace)
            console.log("✅ Adding new message to list:", data.message!.id);
            return [...prev, data.message!];
          });
        }
        // Refresh conversations to update last message preview
        fetchConversations();
      }
    });

    socket.on("customer:conversation:new_message", () => {
      console.log("📬 New conversation message event");
      fetchConversations();
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setWsConnected(false);
    };
  }, [token, selectedConversationId, fetchConversations]);

  // Fetch conversations list (only on initial load and when WebSocket events trigger)
  useEffect(() => {
    fetchConversations();
    // Removed continuous polling - only fetch when WebSocket events occur
  }, [fetchConversations]);

  // Join/leave conversation room when selection changes
  useEffect(() => {
    if (!socketRef.current || !wsConnected) return;
    
    const socket = socketRef.current;
    
    // Join new conversation room
    if (selectedConversationId) {
      socket.emit("join:conversation", { conversationId: selectedConversationId });
      console.log(`📥 Joined conversation room: ${selectedConversationId}`);
    }
    
    // Cleanup: leave room when component unmounts or conversation changes
    return () => {
      if (selectedConversationId && socket.connected) {
        socket.emit("leave:conversation", { conversationId: selectedConversationId });
        console.log(`📤 Left conversation room: ${selectedConversationId}`);
      }
    };
  }, [selectedConversationId, wsConnected]);

  // Fetch messages on initial load when conversation is selected
  // WebSocket will handle all real-time updates (new messages are appended, not replacing the list)
  useEffect(() => {
    if (!selectedConversationId) {
      setMessages([]);
      return;
    }
    
    // Load initial messages via API when conversation is selected
    // This ensures we have all existing messages even if WebSocket isn't connected yet
    console.log(`📥 Loading initial messages for conversation ${selectedConversationId}`);
    fetchMessages();
    
    // Note: WebSocket events will add new messages to this list in real-time
    // They use setMessages((prev) => [...prev, newMessage]) to append, not replace
  }, [selectedConversationId, fetchMessages]);

  const handleSendMessage = useCallback(async () => {
    if (!selectedConversationId || !messageInput.trim()) {
      return;
    }
    
    const messageText = messageInput.trim();
    setMessageInput("");
    setIsSending(true);

    // Try WebSocket first if connected
    if (wsConnected && socketRef.current) {
      try {
        socketRef.current.emit("customer:message:agent_send", {
          conversationId: selectedConversationId,
          message: messageText,
        });
        // Optimistically add message (will be confirmed by socket event)
        setIsSending(false);
        return;
      } catch (error) {
        console.error("WebSocket send failed, falling back to API:", error);
        // Fall through to API fallback
      }
    }

    // Fallback to REST API (always works)
    try {
      await API.post(`/api/v1/customer-chat/conversations/${selectedConversationId}/messages`, {
        message: messageText,
      });
      fetchMessages();
      fetchConversations();
    } catch (error: unknown) {
      console.error("Failed to send support message:", error);
    } finally {
      setIsSending(false);
    }
  }, [selectedConversationId, messageInput, wsConnected, fetchMessages, fetchConversations]);

  const handleAssignConversation = useCallback(
    async (conversationId: number) => {
      try {
        await API.post(`/api/v1/customer-chat/conversations/${conversationId}/assign`);
        fetchConversations();
        setSelectedConversationId(conversationId);
      } catch (error: unknown) {
        console.error("Failed to assign conversation:", error);
      }
    },
    [fetchConversations]
  );

  const filteredConversations = useMemo(() => {
    if (!searchTerm.trim()) {
      return conversations;
    }
    return conversations.filter((conv) =>
      conv.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (conv.customer_email || "").toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [conversations, searchTerm]);

  const pendingConversations = useMemo(
    () => filteredConversations.filter((conv) => conv.status === "pending"),
    [filteredConversations]
  );

  const assignedConversations = useMemo(
    () => filteredConversations.filter((conv) => conv.status !== "pending"),
    [filteredConversations]
  );

  return {
    conversations,
    assignedConversations,
    pendingConversations,
    filteredConversations,
    selectedConversationId,
    setSelectedConversationId,
    isLoadingConversations,
    conversationError,
    messages,
    isLoadingMessages,
    messagesError,
    messageInput,
    setMessageInput,
    isSending,
    searchTerm,
    setSearchTerm,
    handleSendMessage,
    handleAssignConversation,
    fetchMessages,
    fetchConversations,
  };
};

